import React, { useState } from 'react';
import { 
  FaGem, FaShieldAlt, FaBalanceScale, FaCheckCircle, 
  FaChevronDown, FaLandmark, FaBriefcase, FaChartLine, FaUsers 
} from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const WealthManagement = () => {
  // State for Accordion FAQ toggling
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: "What is Wealth Management and how does it differ from standard financial planning?",
      a: "Wealth management is a holistic, high-touch advisory service that goes beyond basic budgeting or investment picking. It integrates asset management, tax optimization, estate planning, retirement structuring, risk management, and philanthropic advisory into a single, comprehensive strategy tailored for high-net-worth individuals."
    },
    {
      q: "Who is eligible for Lotoswealth's Wealth Management services?",
      a: "Our private wealth management services are tailored for High Net Worth Individuals (HNIs), Ultra-HNIs, corporate executives, successful entrepreneurs, family offices, and NRIs seeking sophisticated, institutional-grade financial guidance."
    },
    {
      q: "How do you customize a wealth management strategy?",
      a: "We begin with an in-depth discovery session to understand your current financial standing, risk tolerance, family dynamics, lifestyle goals, and legacy aspirations. From there, our multidisciplinary team crafts a bespoke roadmap designed to protect and compound your wealth across generations."
    },
    {
      q: "What makes Lotoswealth's advisory approach unique?",
      a: "We combine unbiased research, rigorous due diligence, strict confidentiality, and a client-first fiduciary mindset. We don't just sell products; we act as your dedicated financial orchestrator, coordinating seamlessly with tax experts, legal counsels, and asset specialists."
    }
  ];

  return (
    <main className="wealth-management-page">

      <Helmet>
        {/* Basic SEO */}
        <title>Wealth Management Services in Hyderabad | Wealth Management Services | Wealth Advisors | Lotoswealth</title>

        <meta
          name="description"
          content="Lotoswealth provides professional Wealth Management Services in Hyderabad, helping individuals, families, NRIs, and businesses grow, protect, and preserve their wealth through personalized investment strategies, portfolio management, tax planning, retirement planning, estate planning, and financial advisory."
        />

        <meta
          name="keywords"
          content="Wealth Management Services Hyderabad, Wealth Management Services, Wealth Management Hyderabad, Wealth Advisors Hyderabad, Financial Advisor Hyderabad, Investment Advisor Hyderabad, Wealth Planning Hyderabad, Portfolio Management Hyderabad, Portfolio Management Services Hyderabad, PMS Hyderabad, Asset Management Hyderabad, Financial Planning Hyderabad, Retirement Planning Hyderabad, Estate Planning Hyderabad, Tax Planning Hyderabad, Investment Planning Hyderabad, Mutual Funds Hyderabad, Stocks Investment Hyderabad, Alternative Investments Hyderabad, Private Equity Hyderabad, Private Credit Hyderabad, Offshore Investments Hyderabad, NRI Wealth Management Hyderabad, Family Wealth Management Hyderabad, Wealth Protection Hyderabad, Lotoswealth"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Lotoswealth" />
        <meta name="language" content="English" />

        <link rel="canonical" href="https://www.lotoswealth.com/wealth-management" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Lotoswealth" />
        <meta property="og:url" content="https://www.lotoswealth.com/wealth-management" />
        <meta
          property="og:title"
          content="Wealth Management Services in Hyderabad | Lotoswealth"
        />
        <meta
          property="og:description"
          content="Expert Wealth Management Services in Hyderabad offering personalized investment strategies, portfolio management, tax planning, retirement planning, estate planning, and wealth protection."
        />
        <meta
          property="og:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Wealth Management Services in Hyderabad | Lotoswealth"
        />
        <meta
          name="twitter:description"
          content="Achieve your financial goals with Lotoswealth's expert Wealth Management Services in Hyderabad. Personalized investment, portfolio management, retirement planning, and wealth advisory."
        />
        <meta
          name="twitter:image"
          content="https://www.lotoswealth.com/images/lotoswealth-logo.png"
        />
      </Helmet>
      
      {/* =========================================
          1. HERO SECTION (With Ticker & Slow Zoom)
      ========================================= */}
      <div className="hero-wrapper position-relative" style={{ height: '100vh', minHeight: '800px', overflow: 'hidden' }}>
        
        <div className="position-absolute top-0 start-0 w-100 h-100 z-0 animate-slow-zoom">
          <img 
            src="/images/wealth-management.jpg" 
            alt="Wealth Management Services" 
            className="w-100 h-100 object-fit-cover"
          />
        </div>

        {/* Slanted Panels Overlay */}
        <div className="panels-container position-absolute top-0 start-0 w-100 h-100 z-1 d-flex">
          <div className="slanted-panel panel-1"></div>
          <div className="slanted-panel panel-2"></div>
          <div className="slanted-panel panel-3"></div>
          <div className="slanted-panel panel-4"></div>
        </div>

        {/* --- Floating Main Header Text (Perfectly Centered) --- */}
        <div className="container hero-main-text text-center position-absolute top-50 start-50 translate-middle z-3 w-100 px-4">
          <h1 className="fw-bold display-4 animate-slide-up delay-1 mb-3 text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
             Wealth Management
          </h1>
          <h2 className="animate-slide-up delay-2 fw-bold text-white mb-4" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Holistic Advisory to Grow, Protect, and Transfer Your Legacy
          </h2>
          <p className="hero-description animate-slide-up delay-3 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Welcome to Lotoswealth. You have spent a lifetime building your success—now it is time to ensure your wealth works with the same dedication. Our holistic Wealth Management services integrate every dimension of your financial life, from strategic asset allocation and tax structuring to estate planning and generational wealth preservation.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-4 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            We provide high-touch, private client advisory designed for individuals and families who demand institutional-grade expertise, absolute discretion, and customized roadmaps that adapt to changing economic landscapes and personal milestones.
          </p>
          <p className="hero-description mt-3 animate-slide-up delay-5 text-white mx-auto" style={{ maxWidth: '950px', fontSize: '1.1rem', lineHeight: '1.7', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
            Entrust your financial future to seasoned wealth strategists who put your objectives at the absolute center of everything we do.
          </p>
        </div>

        {/* --- Scrolling Stock Ticker --- */}
        <div className="stock-ticker position-absolute bottom-0 w-100 z-3 bg-dark text-white border-top border-bottom border-secondary py-2" style={{ opacity: 0.9 }}>
          <div className="ticker-track">
            {/* First Set */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
            
            {/* Duplicated Set for Infinite Scrolling */}
            <div className="ticker-item text-white border-end border-secondary"><strong>APOLLOHOSP</strong> 8,894.00 <span className="text-danger">-63.00 (-0.70%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>ASIANPAINT</strong> 2,768.60 <span className="text-success">21.30 (0.78%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>AXISBANK</strong> 1,250.00 <span className="text-success">20.50 (1.67%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJ-AUTO</strong> 11,519.00 <span className="text-danger">-2.00 (-0.02%)</span></div>
            <div className="ticker-item text-white border-end border-secondary"><strong>BAJAJFINSV</strong> 2,085.60 <span className="text-success">56.50 (2.78%)</span></div>
          </div>
        </div>
      </div>

      {/* =========================================
          2. WHY WEALTH MANAGEMENT MATTERS (Soft Teal Theme)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f0f9f6' }}>
        <div className="container py-4">
          <div className="row align-items-center g-5">
            <div className="col-lg-6 animate-slide-up delay-1">
              <h2 className="fw-bold mb-4 text-dark">
                Why Holistic Wealth Management is Essential
              </h2>
              <p className="text-muted line-height-lg">
                As wealth grows, its complexity multiplies. Managing investments, tax filings, real estate holdings, risk exposure, and estate planning in isolation often leads to inefficiencies, missed opportunities, and unnecessary vulnerabilities.
              </p>
              <p className="text-muted line-height-lg">
                Holistic wealth management brings all these moving parts into a unified, synchronized strategy. By looking at your complete financial ecosystem, we eliminate blind spots, optimize tax efficiencies, and ensure every asset works in harmony toward your long-term vision.
              </p>
            </div>
            <div className="col-lg-6 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm border-start border-teal border-5">
                <h4 className="fw-bold text-teal mb-3">Core Pillars of Our Wealth Advisory</h4>
                <ul className="list-unstyled text-muted mb-0">
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Holistic Integration:</strong> Unifying investments, taxes, and estate plans.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Tailored Strategies:</strong> Bespoke roadmaps built around your exact life goals.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Risk Mitigation:</strong> Proactive capital protection across economic cycles.</li>
                  <li className="mb-2"><FaCheckCircle className="text-teal me-2" /> <strong>Generational Focus:</strong> Safeguarding assets for children and future heirs.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          3. OUR WEALTH SOLUTIONS (Crisp White)
      ========================================= */}
      <section className="py-5 bg-white" style={{ backgroundImage: "url('/images/lotoswealth-bg.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
        
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">💎 Our Wealth Management Services</h2>
            <p className="text-white">Comprehensive, institutional-grade solutions for private clients.</p>
          </div>

          <div className="row g-4">
            
            <div className="col-lg-4 col-md-6 animate-slide-up delay-1">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaBalanceScale size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Holistic Financial Advisory</h5>
                <p className="text-muted small">Integrating your cash flows, liabilities, investments, and insurance into a singular, synchronized blueprint for lifelong financial success.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-2">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaChartLine size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Multi-Asset Portfolio Management</h5>
                <p className="text-muted small">Active asset allocation across equities, fixed income, alternative investment funds (AIFs), private equity, and global markets.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-3">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaGem size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Tax Optimization & Structuring</h5>
                <p className="text-muted small">Year-round tax planning and capital gains management to minimize liabilities and maximize your net in-hand returns.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-4">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaLandmark size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Estate & Succession Planning</h5>
                <p className="text-muted small">Structuring Wills, private family trusts, and multi-generational transfer strategies to protect your legacy and prevent disputes.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-5">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaBriefcase size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Family Office Services</h5>
                <p className="text-muted small">Dedicated governance, administration, and consolidated asset oversight tailored for ultra-high-net-worth families.</p>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 animate-slide-up delay-6">
              <div className="p-4 bg-light rounded-4 h-100 hover-lift-card border-bottom border-teal border-4">
                <FaShieldAlt size={36} className="text-teal mb-3" />
                <h5 className="fw-bold mb-2">Risk Management & Insurance</h5>
                <p className="text-muted small">Comprehensive risk audits covering life, health, liability, and asset protection to shield your portfolio against unforeseen shocks.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          4. OUR 5-STEP WEALTH PROCESS (Dark Slate)
      ========================================= */}
      <section className="py-5 text-white" style={{ backgroundColor: '#0a192f' }}>
        <div className="container py-4">
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-white">🔄 Our Wealth Management Process</h2>
            <p className="text-light opacity-75">A disciplined, high-touch framework to secure your financial future.</p>
          </div>

          <div className="row g-4">
            
            <div className="col-md-6 col-lg-4 animate-slide-up delay-1">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 1 – Discovery & Vision</h5>
                <p className="small text-muted mb-0">We conduct an in-depth consultation to understand your financial standing, family dynamics, and long-term aspirations.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-2">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 2 – Comprehensive Audit</h5>
                <p className="small text-muted mb-0">Analyzing your existing investments, tax liabilities, cash flows, and risk exposures to identify gaps and opportunities.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-4 animate-slide-up delay-3">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 3 – Bespoke Strategy Design</h5>
                <p className="small text-muted mb-0">Formulating a holistic wealth blueprint encompassing asset allocation, tax efficiency, and estate preservation.</p>
              </div>
            </div>

            <div className="col-md-6 col-lg-6 animate-slide-up delay-4">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 4 – Coordinated Execution</h5>
                <p className="small text-muted mb-0">Implementing the strategy seamlessly across investment portfolios, legal structures, and insurance frameworks.</p>
              </div>
            </div>

            <div className="col-md-12 col-lg-6 animate-slide-up delay-5">
              <div className="p-4 bg-white text-dark rounded-4 shadow-sm h-100 border-top border-4 border-teal">
                <h5 className="fw-bold text-teal mb-2">Step 5 – Ongoing Review & Governance</h5>
                <p className="small text-muted mb-0">Continuous portfolio monitoring, milestone tracking, and annual strategy reviews to adapt to market and life changes.</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          5. WHO WE SERVE & WHY CHOOSE US (Soft Blue-Gray)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#eef2f5' }}>
        <div className="container py-4">
          <div className="row g-5">
            
            {/* Target Audience */}
            <div className="col-lg-5 animate-slide-up delay-1">
              <h3 className="fw-bold mb-4 text-dark">👥 Who Benefits from Wealth Management?</h3>
              <p className="text-muted small mb-4">Our private client services are tailored for individuals with complex financial needs:</p>
              <div className="d-flex flex-wrap gap-2">
                {[
                  "High Net Worth Individuals (HNIs)", "Ultra-HNIs & Family Offices",
                  "Business Owners & Promoters", "Corporate Executives & Founders",
                  "Successful Professionals", "NRIs Managing Cross-Border Wealth",
                  "Retirees with Substantial Corpus", "Multi-Generational Families"
                ].map((item, idx) => (
                  <span key={idx} className="badge bg-white text-dark border p-2 shadow-sm fs-6">
                    {item}
                  </span>
                ))}
              </div>
            </div>

            {/* Why Choose Lotoswealth */}
            <div className="col-lg-7 animate-slide-up delay-2">
              <div className="p-4 bg-white rounded-4 shadow-sm h-100">
                <h3 className="fw-bold mb-3 text-dark">🏆 Why Trust Lotoswealth?</h3>
                <p className="text-muted small mb-4">Private wealth management requires unwavering fiduciary responsibility and profound expertise. Clients choose us because we offer:</p>
                <div className="row g-3">
                  {[
                    "Holistic, 360-Degree Financial Advisory", "Absolute Discretion & Confidentiality",
                    "Dedicated Private Client Advisors", "Institutional-Grade Investment Access",
                    "Customized Tax & Estate Structuring", "Transparent Fee & Advisory Models",
                    "Proactive Portfolio Risk Governance", "Long-Term Generational Focus"
                  ].map((item, idx) => (
                    <div key={idx} className="col-md-6 d-flex align-items-center">
                      <FaCheckCircle className="text-success me-2 flex-shrink-0" />
                      <span className="small text-muted fw-bold">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* =========================================
          6. FREQUENTLY ASKED QUESTIONS (FAQ)
      ========================================= */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container py-4" style={{ maxWidth: '850px' }}>
          <div className="text-center mb-5 animate-slide-up delay-1">
            <h2 className="fw-bold text-dark">❓ Frequently Asked Questions</h2>
            <p className="text-muted">Common queries regarding Private Wealth Management Services</p>
          </div>

          <div className="d-flex flex-column gap-3 animate-slide-up delay-2">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="bg-white rounded-3 shadow-sm border overflow-hidden"
              >
                <button 
                  className="w-100 text-start p-3 border-0 bg-white d-flex justify-content-between align-items-center fw-bold text-dark"
                  onClick={() => toggleFaq(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="pe-3">{faq.q}</span>
                  <FaChevronDown 
                    className={`text-teal transition-transform ${openFaq === index ? 'rotate-180' : ''}`} 
                    style={{ transition: 'transform 0.3s ease', minWidth: '16px' }}
                  />
                </button>
                {openFaq === index && (
                  <div className="p-3 pt-0 text-muted small border-top bg-light lh-lg">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          7. FINAL CTA SECTION (Deep Navy)
      ========================================= */}
      <section className="py-5 text-white text-center" style={{ backgroundColor: '#0f1e2d' }}>
        <div className="container py-4">
          <h2 className="fw-bold mb-3 animate-slide-up delay-1">Ready to Elevate Your Wealth Strategy?</h2>
          <p className="text-light opacity-75 mb-4 animate-slide-up delay-2 mx-auto" style={{ maxWidth: '600px' }}>
            Experience private wealth management built around your unique aspirations and legacy. Connect with our senior advisors today.
          </p>
          <button className="btn-custom-teal hover-lift px-5 py-3 fs-5 border-0 rounded text-white shadow animate-slide-up delay-3">
            Schedule a Private Wealth Consultation
          </button>
        </div>
      </section>

    </main>
  );
};

export default WealthManagement;